import React from 'react';
import site from '../../data/site';
import { Section, SectionHeading } from '../ui/Section';
import Reveal from '../ui/Reveal';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import DotTrail from '../ui/DotTrail';

/**
 * Long unbroken strings (the address is a single 35-character email) otherwise
 * break mid-word inside a narrow card, which reads as a typo. Offer the
 * browser a break opportunity after the "@" instead.
 */
function Wrappable({ value }) {
  const at = value.indexOf('@');
  if (at === -1) return value;
  return (
    <>
      {value.slice(0, at + 1)}
      <wbr />
      {value.slice(at + 1)}
    </>
  );
}

const channels = [
  {
    icon: 'phone',
    label: 'Call the showroom',
    value: site.contact.phoneDisplay,
    href: site.contact.phoneHref,
  },
  {
    icon: 'whatsapp',
    label: 'WhatsApp',
    value: 'Send an enquiry',
    href: site.contact.whatsappHref,
    external: true,
  },
  {
    icon: 'mail',
    label: 'Email',
    value: site.contact.email,
    href: `mailto:${site.contact.email}`,
  },
  {
    icon: 'instagram',
    label: 'Instagram',
    value: site.social.instagramHandle,
    href: site.social.instagram,
    external: true,
  },
];

/** Address, hours, contact channels and an embedded map. */
export function Visit() {
  return (
    <Section id="visit" tone="canvas">
      <div className="shell">
        <SectionHeading
          eyebrow="Visit"
          title="Find us on C G Road"
          lede="Opposite Lal Bunglow, Ahmedabad. Call ahead if you would like a particular collection kept aside — someone will have it ready."
          align="center"
        />

        <div className="mt-14 grid gap-6 lg:grid-cols-[1.15fr_1fr] lg:gap-8">
          {/* ---- Map ---- */}
          <Reveal
            variant="left"
            distance={30}
            duration={950}
            className="overflow-hidden rounded-sm border border-ink-900/10 bg-canvas-soft shadow-card"
          >
            <iframe
              title={`Map to ${site.legalName}`}
              src={site.address.mapsEmbed}
              className="h-[22rem] w-full border-0 lg:h-full lg:min-h-[30rem]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </Reveal>

          {/* ---- Details ---- */}
          <div className="flex flex-col gap-6">
            <Reveal
              variant="right"
              distance={30}
              duration={950}
              delay={80}
              className="rounded-sm bg-ink-950 p-8 text-canvas sm:p-9"
            >
              <DotTrail count={5} tone="gold" />

              <address className="mt-6 not-italic">
                <p className="eyebrow mb-3 text-champagne-300">The Showroom</p>
                <p className="font-display text-2xl leading-snug">
                  {site.address.line1}
                  <br />
                  {site.address.line2}
                </p>
                <p className="mt-2 text-sm text-canvas/55">
                  {site.address.city}, {site.address.state} {site.address.postalCode}
                </p>
              </address>

              <div className="mt-7 border-t border-canvas/10 pt-6">
                <p className="eyebrow mb-3 flex items-center gap-2 text-champagne-300">
                  <Icon name="clock" size={14} />
                  Hours
                </p>
                <ul className="space-y-1.5 text-sm text-canvas/60">
                  {site.hours.map((slot) => (
                    <li key={slot.days} className="flex justify-between gap-6">
                      <span>{slot.days}</span>
                      <span className="text-canvas/80">{slot.time}</span>
                    </li>
                  ))}
                </ul>
                {!site.hoursConfirmed && (
                  <p className="mt-3 text-[0.7rem] leading-relaxed text-canvas/35">
                    Please call to confirm before travelling.
                  </p>
                )}
              </div>

              <Button
                href={site.address.mapsHref}
                variant="gold"
                size="md"
                className="mt-7 w-full"
              >
                <Icon name="pin" size={16} />
                Get Directions
              </Button>
            </Reveal>

            <ul className="grid gap-3 sm:grid-cols-2">
              {channels.map((channel, i) => (
                <Reveal
                  as="li"
                  key={channel.label}
                  variant="up"
                  distance={20}
                  delay={160 + i * 80}
                >
                  <a
                    href={channel.href}
                    {...(channel.external
                      ? { target: '_blank', rel: 'noreferrer noopener' }
                      : {})}
                    className="group flex h-full items-start gap-3.5 rounded-sm border border-ink-900/12 bg-canvas p-5 transition-all duration-300 ease-silk hover:-translate-y-0.5 hover:border-champagne-300 hover:shadow-card"
                  >
                    <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-canvas-soft text-sun-500 transition-colors group-hover:bg-sunrise group-hover:text-ink-950">
                      <Icon name={channel.icon} size={16} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[0.6rem] uppercase tracking-widest text-ink-700/55">
                        {channel.label}
                      </span>
                      <span className="mt-1 block break-words text-[0.88rem] text-ink-950">
                        <Wrappable value={channel.value} />
                      </span>
                    </span>
                  </a>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Section>
  );
}

export default Visit;
